/**
 * The editor's building blocks for a reply:
 *
 *   fieldChip      a piece of information code fills in ("Client's name"), shown as a chip
 *   condition      "This part changes depending on whether …", holding two sides
 *   conditionSide  the "If …" or "Otherwise" side: paragraphs only, so conditions never nest
 *   legalNote      counsel's words, plain text only, sent in small print
 *
 * What each looks like is in `components/editor/`. The catalog (labels, conditions) is passed in
 * as options, so the chips always show plain words.
 */
import { Node, mergeAttributes } from '@tiptap/core';
import Document from '@tiptap/extension-document';
import HardBreak from '@tiptap/extension-hard-break';
import Paragraph from '@tiptap/extension-paragraph';
import Text from '@tiptap/extension-text';
import { Dropcursor, Gapcursor, Placeholder, TrailingNode, UndoRedo } from '@tiptap/extensions';
import { ReactNodeViewRenderer, type Extensions } from '@tiptap/react';
import type { ReplyCatalog } from '../api/api';
import { ConditionSideView, ConditionView } from '../components/editor/ConditionView';
import { FieldChipView } from '../components/editor/FieldChipView';
import { LegalNoteView } from '../components/editor/LegalNoteView';

/** What a dragged chip carries. */
export const FIELD_DRAG_TYPE = 'application/x-waterlily-field';

export interface ReplyNodeOptions {
  catalog: ReplyCatalog;
}

export const FieldChip = Node.create<ReplyNodeOptions>({
  name: 'fieldChip',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,
  draggable: true,
  addOptions: () => ({ catalog: { fields: [], conditions: [], steps: [], owners: [] } }),
  addAttributes: () => ({ name: { default: '' } }),
  parseHTML: () => [
    {
      tag: 'span[data-field]',
      getAttrs: element => ({ name: element.getAttribute('data-field') }),
    },
  ],
  renderHTML: ({ node, HTMLAttributes }) => [
    'span',
    mergeAttributes(HTMLAttributes, { 'data-field': node.attrs.name }),
    node.attrs.name,
  ],
  renderText: ({ node }) => `[${node.attrs.name}]`,
  addNodeView: () => ReactNodeViewRenderer(FieldChipView),
});

export const Condition = Node.create<ReplyNodeOptions>({
  name: 'condition',
  group: 'block',
  content: 'conditionSide conditionSide',
  defining: true,
  isolating: true,
  addOptions: () => ({ catalog: { fields: [], conditions: [], steps: [], owners: [] } }),
  addAttributes: () => ({ flag: { default: 'intake_complete' } }),
  parseHTML: () => [
    {
      tag: 'div[data-condition]',
      getAttrs: element => ({ flag: element.getAttribute('data-condition') }),
    },
  ],
  renderHTML: ({ node, HTMLAttributes }) => [
    'div',
    mergeAttributes(HTMLAttributes, { 'data-condition': node.attrs.flag }),
    0,
  ],
  addNodeView: () => ReactNodeViewRenderer(ConditionView),
});

export const ConditionSide = Node.create({
  name: 'conditionSide',
  content: 'paragraph+',
  defining: true,
  isolating: true,
  addAttributes: () => ({ side: { default: 'then' } }),
  parseHTML: () => [
    {
      tag: 'div[data-condition-side]',
      getAttrs: element => ({ side: element.getAttribute('data-condition-side') }),
    },
  ],
  renderHTML: ({ node, HTMLAttributes }) => [
    'div',
    mergeAttributes(HTMLAttributes, { 'data-condition-side': node.attrs.side }),
    0,
  ],
  addNodeView: () => ReactNodeViewRenderer(ConditionSideView),
});

export const LegalNote = Node.create({
  name: 'legalNote',
  group: 'block',
  content: 'text*',
  marks: '',
  defining: true,
  isolating: true,
  parseHTML: () => [{ tag: 'div[data-legal-note]', preserveWhitespace: 'full' }],
  renderHTML: ({ HTMLAttributes }) => [
    'div',
    mergeAttributes(HTMLAttributes, { 'data-legal-note': '' }),
    0,
  ],
  addNodeView: () => ReactNodeViewRenderer(LegalNoteView),
  addKeyboardShortcuts() {
    return {
      // A legal note is one stretch of text: Enter leaves it for a new paragraph below.
      Enter: ({ editor }) => {
        const { $from } = editor.state.selection;
        if ($from.parent.type.name !== 'legalNote') return false;
        const after = $from.after();
        return editor
          .chain()
          .insertContentAt(after, { type: 'paragraph' })
          .setTextSelection(after + 1)
          .run();
      },
    };
  },
});

export function replyExtensions(catalog: ReplyCatalog): Extensions {
  return [
    Document.extend({ content: 'block+' }),
    Paragraph,
    Text,
    HardBreak,
    UndoRedo,
    Gapcursor,
    TrailingNode,
    Dropcursor.configure({ color: '#5B3FD6', width: 2 }),
    Placeholder.configure({
      includeChildren: true,
      placeholder: ({ node }) =>
        node.type.name === 'legalNote'
          ? 'Type the legal wording…'
          : 'Type here, or add information from above…',
    }),
    FieldChip.configure({ catalog }),
    Condition.configure({ catalog }),
    ConditionSide,
    LegalNote,
  ];
}
